import * as React from "react";

import {
  Box,
  makeStyles,
  TextField,
  Theme,
  Typography,
} from "@material-ui/core";
import ReCAPTCHA from "react-google-recaptcha";

import { MopButton } from "client/components";
import { useBranding } from "client/hooks/useBranding";
import { useLogin } from "client/hooks/useLogin";
import { useNormalizedBrandPaletteValues } from "client/hooks/useNormalizedBrandThemeValues";
import { useTranslation } from "client/hooks/useTranslation";
import { getCaptchaSiteKey, isCaptchaApproved } from "client/utils/captcha";
import { isValidEmail } from "client/utils/validator";

const useStyles = makeStyles((theme: Theme) => {
  const { error } = useNormalizedBrandPaletteValues(theme);

  return {
    emailErrorText: {
      margin: "0.8rem 0 0",
      color: error,
    },
    fullWidth: {
      width: "100%",
    },
  };
});

const emailHeaderDefaultCopy =
  "Enter your email address to get started, or pick up where you left off.";

type Props = {
  heading?: React.ReactNode;
  emailHelperText?: React.ReactNode;
  emailHeader?: React.ReactNode;
  leftAlign?: boolean;
};

export const LoginForm: React.FC<Props> = ({
  emailHeader = emailHeaderDefaultCopy,
  emailHelperText = null,
  heading = null,
  leftAlign = false,
}) => {
  const [error, setError] = React.useState("");

  const t = useTranslation();
  const { brandTitle, Logo, brandKey } = useBranding();
  const logoHeight = brandKey === "CashApp" ? 55 : 46;
  const classes = useStyles();

  const [isCaptchaChecked, setIsCaptchaChecked] = React.useState(() => {
    // bypass captcha in dev mode
    if (process.env.NODE_ENV !== "production") {
      return true;
    }
    return false;
  });

  const { email, setEmail, login, submitCaptcha } = useLogin({
    onError: setError,
    onCaptchaResult: setIsCaptchaChecked,
  });

  const submitDisabled = email === "" || !isCaptchaApproved(isCaptchaChecked);

  function handleSubmit(event: React.SyntheticEvent<HTMLButtonElement>) {
    event.preventDefault();
    const trimmedEmail = email.trim();
    if (!isValidEmail(trimmedEmail)) {
      setError(t("invalidEmailError"));
      return;
    }

    login(trimmedEmail);
  }

  return (
    <Box
      alignItems="center"
      display="flex"
      flexDirection="column"
      height="100%"
      width="100%"
    >
      <Box
        alignItems={leftAlign ? "initial" : "center"}
        display="flex"
        flexDirection="column"
        width="304px"
      >
        <Box mt={4} width="80%">
          <Logo alt={`${brandTitle} logo`} height={logoHeight} width="100%" />
        </Box>
        {heading ? <Box mt={4}>{heading}</Box> : null}
        <Box mt={4}>
          <Typography>{emailHeader}</Typography>
        </Box>
        <Box mt={4} width="100%">
          <TextField
            autoComplete="email"
            className={classes.fullWidth}
            error={error !== ""}
            id="emailTextField"
            label="Enter your email"
            name="email"
            onChange={(e) => setEmail(e.currentTarget.value.trim())}
            onFocus={() => setError("")}
            placeholder="email@example.com"
            type="text"
            value={email}
            variant="outlined"
          />
          {emailHelperText ? <Box mt={2}>{emailHelperText}</Box> : null}
          {error && (
            <Typography className={classes.emailErrorText}>{error}</Typography>
          )}
        </Box>
        <Box mt={4}>
          <ReCAPTCHA
            theme="light"
            sitekey={getCaptchaSiteKey()}
            onChange={submitCaptcha}
          />
        </Box>
        <Box mt={3} mb={1} width="100%">
          <MopButton
            className={classes.fullWidth}
            mopVariant="primary"
            disabled={submitDisabled}
            onClick={handleSubmit}
          >
            {t("uiLoginFormSubmitButtonText")}
          </MopButton>
        </Box>
      </Box>
    </Box>
  );
};
